/**
 * Document Viewer Component
 *
 * PDF viewer with signature field indicators.
 * Features:
 * - PDF rendering with react-pdf
 * - Mobile-optimized scrolling and zoom
 * - Signature field indicators with "click to sign" overlay
 * - Navigate to next signature field
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ZoomIn,
  ZoomOut,
  RotateCw,
  ChevronDown,
  PenTool,
  Type,
  CheckSquare,
  Image as ImageIcon,
  Loader2,
} from 'lucide-react';
import { Button } from '@respectly/ui';
import type { SignerFieldValue } from './SignerFieldList';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface SignatureField {
  id: string;
  label: string;
  required: boolean;
  /** 'signature' | 'initials' | 'text' | 'checkbox' | 'dropdown' | 'image' (defaults to signature) */
  fieldType?: string;
  signed?: boolean;
  position?: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  };
  // Editor-configured properties — see DocumentFieldPropertiesPanel in user-tenant.
  placeholder?: string;
  helpText?: string;
  defaultValue?: string | boolean;
  options?: Array<{ label: string; value: string }>;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    customMessage?: string;
  };
}

interface TextField {
  id: string;
  variableName: string;
  value: string;
  position: {
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

interface DocumentViewerProps {
  fileUrl: string;
  pageCount?: number;
  signatureFields: SignatureField[];
  textFields?: TextField[];
  onSignatureFieldClick: (field: SignatureField) => void;
  signedFields: Map<string, SignerFieldValue>;
  setSignedFields: Dispatch<SetStateAction<Map<string, SignerFieldValue>>>;
}

export function DocumentViewer({
  fileUrl,
  signatureFields,
  textFields = [],
  onSignatureFieldClick,
  signedFields,
  setSignedFields,
}: DocumentViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1);
  const [containerWidth, setContainerWidth] = useState(0);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  // Update container width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setLoadError(null);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error('PDF load error:', error);
    setLoadError('Failed to load document. Please try again.');
    setIsLoading(false);
  }, []);

  const zoomIn = () => setScale((s) => Math.min(s + 0.25, 3));
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));
  const resetZoom = () => setScale(1);

  // Scroll to a signature field
  const scrollToField = (field: SignatureField) => {
    if (!field.position) return;

    const pageElement = pageRefs.current.get(field.position.page);
    if (pageElement) {
      pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  // Find the next unsigned signature/initials field (other types are filled inline)
  const getNextUnsignedField = () => {
    return signatureFields.find((field) => {
      if (!field.required) return false;
      if (signedFields.has(field.id)) return false;
      const ft = field.fieldType || 'signature';
      return ft === 'signature' || ft === 'initials';
    });
  };

  const nextField = getNextUnsignedField();

  // Get fields for a specific page
  const getFieldsForPage = (pageNum: number) => {
    return signatureFields.filter((field) => field.position?.page === pageNum);
  };

  // Get text fields for a specific page
  const getTextFieldsForPage = (pageNum: number) => {
    return textFields.filter((field) => field.position?.page === pageNum);
  };

  // Calculate PDF width based on container
  const pdfWidth = Math.min(containerWidth - 32, 800) * scale;

  return (
    <div className="flex h-full flex-col bg-gray-100">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-4 py-2 shadow-sm">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
            className="h-8 w-8 p-0"
          >
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="min-w-[3rem] text-center text-sm text-gray-600">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3}
            className="h-8 w-8 p-0"
          >
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetZoom}
            className="h-8 w-8 p-0"
          >
            <RotateCw className="h-4 w-4" />
          </Button>
        </div>

        {nextField && (
          <Button
            variant="default"
            size="sm"
            onClick={() => {
              scrollToField(nextField);
              onSignatureFieldClick(nextField);
            }}
            className="gap-2"
          >
            <PenTool className="h-4 w-4" />
            <span className="hidden sm:inline">Sign next field</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        )}

        <div className="text-sm text-gray-500">
          {numPages && `${numPages} page${numPages > 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Document Container */}
      <div
        ref={containerRef}
        className="flex-1 overflow-auto p-4"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {loadError ? (
          <div className="flex h-full items-center justify-center">
            <div className="rounded-lg bg-red-50 p-6 text-center">
              <p className="text-red-600">{loadError}</p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsLoading(true);
                  setLoadError(null);
                }}
                className="mt-4"
              >
                Try again
              </Button>
            </div>
          </div>
        ) : (
          <div className="mx-auto" style={{ maxWidth: pdfWidth }}>
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                  <span className="ml-3 text-gray-600">Loading document...</span>
                </div>
              }
            >
              {numPages &&
                Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                  <SignerPageWithFields
                    key={pageNum}
                    pageNum={pageNum}
                    totalPages={numPages}
                    pdfWidth={pdfWidth}
                    signatureFields={getFieldsForPage(pageNum)}
                    textFields={getTextFieldsForPage(pageNum)}
                    signedFields={signedFields}
                    setSignedFields={setSignedFields}
                    onSignatureFieldClick={onSignatureFieldClick}
                    registerPageRef={(el) => {
                      if (el) pageRefs.current.set(pageNum, el);
                    }}
                  />
                ))}
            </Document>
          </div>
        )}
      </div>

      {/* Loading overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center bg-white/80"
          >
            <div className="text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary-500" />
              <p className="mt-3 text-gray-600">Loading document...</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SignerPageWithFields — one page + its overlay
// ═══════════════════════════════════════════════════════════════════════════
// The overlay sits in `position: absolute; inset: 0` against the page
// wrapper, so it always tracks the canvas's rendered size. Fields use
// percentage-based CSS so the browser rescales them natively when the
// browser is resized, the user zooms, or the layout reflows on mobile.
interface SignerPageWithFieldsProps {
  pageNum: number;
  totalPages: number;
  pdfWidth: number;
  signatureFields: SignatureField[];
  textFields: TextField[];
  signedFields: Map<string, SignerFieldValue>;
  setSignedFields: Dispatch<SetStateAction<Map<string, SignerFieldValue>>>;
  onSignatureFieldClick: (field: SignatureField) => void;
  registerPageRef: (el: HTMLDivElement | null) => void;
}

function SignerPageWithFields({
  pageNum,
  totalPages,
  pdfWidth,
  signatureFields,
  textFields,
  signedFields,
  setSignedFields,
  onSignatureFieldClick,
  registerPageRef,
}: SignerPageWithFieldsProps) {
  return (
    <div ref={registerPageRef} className="relative mb-4 shadow-lg inline-block">
      <Page
        pageNumber={pageNum}
        width={pdfWidth}
        renderTextLayer={true}
        renderAnnotationLayer={true}
        loading={
          <div
            className="flex items-center justify-center bg-white"
            style={{ height: pdfWidth * 1.41 }}
          >
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        }
      />

      {/* Overlay tracks the page wrapper which is sized by the canvas. */}
      <div
        className="pointer-events-none"
        style={{ position: 'absolute', inset: 0 }}
        data-testid="signer-field-overlay"
      >
        {/* Field Overlays — render per fieldType so the canvas matches the side panel */}
        {signatureFields.map((field) => {
          if (!field.position) return null;
          return (
            <SignerFieldOverlay
              key={field.id}
              field={field}
              fieldValue={signedFields.get(field.id)}
              setSignedFields={setSignedFields}
              onSignatureFieldClick={onSignatureFieldClick}
            />
          );
        })}

        {/* Text Field Overlays (prepopulated values) */}
        {textFields.map((field) => {
          if (!field.position) return null;

          return (
            <div
              key={field.id}
              data-testid={`signer-text-field-${field.id}`}
              className="flex items-center justify-center px-1 text-sm bg-transparent pointer-events-none"
              style={{
                position: 'absolute',
                left: `${field.position.x * 100}%`,
                top: `${field.position.y * 100}%`,
                width: `${field.position.width * 100}%`,
                height: `${field.position.height * 100}%`,
                zIndex: 5,
              }}
            >
              <span className="truncate text-center text-gray-900">{field.value}</span>
            </div>
          );
        })}
      </div>

      {/* Page number indicator */}
      <div className="absolute bottom-2 right-2 rounded bg-black/50 px-2 py-1 text-xs text-white">
        Page {pageNum} of {totalPages}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SignerFieldOverlay — renders one field on the canvas based on its fieldType
// ═══════════════════════════════════════════════════════════════════════════
// Mirrors the per-type rendering in SignerFieldList so the PDF canvas and the
// side panel share one value model (signedFields Map). signature/initials open
// the SignatureCapture modal via onSignatureFieldClick; text/checkbox/image
// update the Map inline.
interface SignerFieldOverlayProps {
  field: SignatureField;
  fieldValue: SignerFieldValue | undefined;
  setSignedFields: Dispatch<SetStateAction<Map<string, SignerFieldValue>>>;
  onSignatureFieldClick: (field: SignatureField) => void;
}

function SignerFieldOverlay({
  field,
  fieldValue,
  setSignedFields,
  onSignatureFieldClick,
}: SignerFieldOverlayProps) {
  if (!field.position) return null;

  const ft = field.fieldType || 'signature';
  const isFilled = fieldValue !== undefined;
  const positionStyle = {
    position: 'absolute' as const,
    left: `${field.position.x * 100}%`,
    top: `${field.position.y * 100}%`,
    width: `${field.position.width * 100}%`,
    height: `${field.position.height * 100}%`,
    zIndex: 10,
  };

  // ── Dropdown ─────────────────────────────────────────────────────────────
  if (ft === 'dropdown') {
    const opts = field.options ?? [];
    return (
      <select
        data-testid={`signer-canvas-field-${field.id}`}
        title={field.helpText}
        value={fieldValue?.data || ''}
        onChange={(e) => {
          const val = e.target.value;
          setSignedFields((prev) => {
            const next = new Map(prev);
            if (val) {
              next.set(field.id, { type: 'text_input', data: val });
            } else {
              next.delete(field.id);
            }
            return next;
          });
        }}
        className={`pointer-events-auto rounded border-2 bg-white/90 px-1 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-300 ${
          isFilled ? 'border-green-500' : 'border-primary-500'
        }`}
        style={positionStyle}
      >
        <option value="">{field.placeholder || field.label || 'Select…'}</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }

  // ── Text input (also fallback for unknown / date) ────────────────────────
  if (ft === 'text' || ft === 'date' || ft === 'date_signed' || ft === 'textarea') {
    return (
      <input
        type="text"
        data-testid={`signer-canvas-field-${field.id}`}
        title={field.helpText}
        value={fieldValue?.data || (typeof field.defaultValue === 'string' ? field.defaultValue : '')}
        placeholder={field.placeholder || field.label}
        maxLength={field.validation?.maxLength}
        pattern={field.validation?.pattern}
        onChange={(e) => {
          const val = e.target.value;
          setSignedFields((prev) => {
            const next = new Map(prev);
            if (val) {
              next.set(field.id, { type: 'text_input', data: val });
            } else {
              next.delete(field.id);
            }
            return next;
          });
        }}
        className={`pointer-events-auto rounded border-2 bg-white/90 px-1 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-300 ${
          isFilled ? 'border-green-500' : 'border-primary-500'
        }`}
        style={positionStyle}
      />
    );
  }

  // ── Checkbox ─────────────────────────────────────────────────────────────
  if (ft === 'checkbox') {
    const isChecked =
      fieldValue !== undefined
        ? fieldValue.data === 'true'
        : field.defaultValue === true;
    return (
      <button
        type="button"
        data-testid={`signer-canvas-field-${field.id}`}
        title={field.helpText}
        onClick={() => {
          setSignedFields((prev) => {
            const next = new Map(prev);
            next.set(field.id, { type: 'checkbox', data: String(!isChecked) });
            return next;
          });
        }}
        className={`pointer-events-auto flex items-center justify-center rounded border-2 transition-colors ${
          isChecked
            ? 'border-green-500 bg-green-50'
            : 'border-primary-500 bg-white/90 hover:bg-primary-50'
        }`}
        style={positionStyle}
        aria-checked={isChecked}
        role="checkbox"
        aria-label={field.label}
      >
        {isChecked && <CheckSquare className="h-4 w-4 text-green-600" />}
      </button>
    );
  }

  // ── Image upload ─────────────────────────────────────────────────────────
  if (ft === 'image') {
    return (
      <label
        data-testid={`signer-canvas-field-${field.id}`}
        className={`pointer-events-auto flex cursor-pointer items-center justify-center overflow-hidden rounded border-2 transition-colors ${
          isFilled
            ? 'border-green-500 bg-white'
            : 'border-primary-500 bg-primary-50/50 hover:bg-primary-100/70'
        }`}
        style={positionStyle}
      >
        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = (ev) => {
              const base64 = ev.target?.result as string;
              setSignedFields((prev) => {
                const next = new Map(prev);
                next.set(field.id, { type: 'image_upload', data: base64 });
                return next;
              });
            };
            reader.readAsDataURL(file);
          }}
        />
        {isFilled && fieldValue ? (
          <img
            src={fieldValue.data}
            alt={field.label}
            className="h-full w-full object-contain p-1"
          />
        ) : (
          <span
            className="flex items-center justify-center gap-1 rounded bg-primary-500 px-2 py-1 text-xs font-medium text-white"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <ImageIcon className="h-3 w-3" />
            <span>Upload image</span>
          </span>
        )}
      </label>
    );
  }

  // ── Signature / initials (default) ───────────────────────────────────────
  const isInitials = ft === 'initials';
  const FieldIcon = isInitials ? Type : PenTool;
  const ctaLabel = isInitials ? 'Click to Initial' : 'Click to Sign';
  const showSignatureImage = isFilled && fieldValue && fieldValue.data?.startsWith('data:');

  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      data-testid={`signer-canvas-field-${field.id}`}
      className={`pointer-events-auto flex cursor-pointer items-center justify-center overflow-hidden rounded border-2 transition-all ${
        isFilled
          ? 'border-green-500 bg-white'
          : 'border-primary-500 bg-primary-50/50 hover:bg-primary-100/70'
      }`}
      style={positionStyle}
      onClick={() => !isFilled && onSignatureFieldClick(field)}
      disabled={isFilled}
    >
      <AnimatePresence mode="wait">
        {showSignatureImage ? (
          <motion.img
            key="signature-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            src={fieldValue.data}
            alt={isInitials ? 'Initials' : 'Signature'}
            className="h-full w-full object-contain p-1"
          />
        ) : isFilled ? (
          <motion.span
            key="signed"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs font-medium text-green-600"
          >
            {isInitials ? 'Initialed' : 'Signed'}
          </motion.span>
        ) : (
          <motion.div
            key="unsigned"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center gap-1 rounded bg-primary-500 px-2 py-1 text-xs font-medium text-white"
            style={{ minWidth: 44, minHeight: 44 }}
          >
            <FieldIcon className="h-3 w-3" />
            <span>{ctaLabel}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
